import { Controller } from "@hotwired/stimulus"
import { formatNumber } from "~/javascript/format_number"

const LABEL_COLOR = "#8A8984"

// Writes each stacked bar's total above it, replacing the y-axis.
export const stackTotalsPlugin = {
  id: "stackTotals",
  afterDatasetsDraw(chart) {
    const metas = chart.data.datasets.map((_, index) => chart.getDatasetMeta(index)).filter(meta => !meta.hidden)
    if (metas.length === 0) return

    const { ctx } = chart
    ctx.save()
    ctx.font = "10px system-ui, sans-serif"
    ctx.fillStyle = LABEL_COLOR
    ctx.textAlign = "center"
    ctx.textBaseline = "bottom"
    chart.data.labels.forEach((_, index) => {
      const total = metas.reduce((sum, meta) => sum + (Number(chart.data.datasets[meta.index].data[index]) || 0), 0)
      if (total === 0) return

      const top = Math.min(...metas.map(meta => meta.data[index]?.y ?? Infinity))
      ctx.fillText(formatNumber(total), metas[0].data[index].x, top - 2)
    })
    ctx.restore()
  }
}

export default class extends Controller {
  static targets = ["chart"]
  static values = { chartData: Object }

  loadChart() { return import("chart.js/auto") }

  async connect() {
    const connection = this.connection = {}
    try {
      const { Chart } = await this.loadChart()
      if (this.connection !== connection) return
      this.chart = new Chart(this.chartTarget, {
        type: "bar",
        data: {
          labels: this.chartDataValue.labels,
          datasets: this.chartDataValue.datasets.map(ds => ({
            label: ds.label,
            data: ds.data,
            backgroundColor: ds.color
          }))
        },
        plugins: [stackTotalsPlugin],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 16 } },
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { position: "bottom", labels: { boxWidth: 9, boxHeight: 9, color: "#5F5E5A", font: { size: 11 } } },
            tooltip: { mode: "index", intersect: false, callbacks: { label: item => `${item.dataset.label}: ${formatNumber(item.parsed.y)}` } }
          },
          scales: {
            x: { stacked: true, grid: { display: false }, border: { display: false }, ticks: { autoSkip: true, maxRotation: 0, color: LABEL_COLOR, font: { size: 10 } } },
            y: { stacked: true, beginAtZero: true, display: false }
          }
        }
      })
    } catch (error) {
      if (this.connection === connection) this.dispatch("error", { detail: { error } })
    }
  }

  disconnect() {
    this.connection = null
    this.chart?.destroy()
    this.chart = null
  }
}
